import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Volume2, VolumeX } from "lucide-react";
import { withV } from "../../lib/media";

const IMG_MS = 5000;
const HOLD_MS = 220;

const StoryThumb = ({ s, i, onOpen }) => (
  <button
    onClick={() => onOpen(i)}
    className="flex shrink-0 flex-col items-center gap-1.5 transition active:scale-95"
    data-testid={`story-thumb-${i}`}
  >
    <span
      className="rounded-full p-[2.5px]"
      style={{ background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)" }}
    >
      <span className="relative block rounded-full border-2 border-black/40 p-[2px]">
        {s.mediaType === "video" && !s.thumb ? (
          <video
            src={withV(`${s.media}#t=0.1`)}
            className="h-16 w-16 rounded-full object-cover"
            muted
            playsInline
            preload="metadata"
          />
        ) : (
          <img src={s.thumb || s.media} alt={s.label || "historia"} className="h-16 w-16 rounded-full object-cover" />
        )}
        {s.mediaType === "video" && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/45 backdrop-blur-sm">
              <span className="ml-0.5 block h-0 w-0 border-y-[6px] border-l-[10px] border-y-transparent border-l-white" />
            </span>
          </span>
        )}
      </span>
    </span>
    {s.label && <span className="max-w-[72px] truncate text-[11px] text-white/70">{s.label}</span>}
  </button>
);

// Visor a pantalla completa estilo Instagram: un solo <video> reutilizado (arranca al instante,
// conserva el sonido en iOS), toque izq/der, mantener = x2, deslizar abajo = cerrar.
const StoryViewer = ({ items, start, onClose }) => {
  const [active, setActive] = useState(start);
  const [fast, setFast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const barRef = useRef(null);
  const elapsed = useRef(0);
  const dirRef = useRef(1);
  const retry = useRef(0);
  const press = useRef(null);
  const fastRef = useRef(false);
  const loadingRef = useRef(false);
  const cur = items[active];
  const isVideo = cur?.mediaType === "video";

  const go = useCallback(
    (d) => {
      dirRef.current = d;
      setActive((a) => {
        const n = a + d;
        if (n >= items.length) {
          onClose();
          return a;
        }
        return Math.max(0, n);
      });
    },
    [items.length, onClose]
  );

  useEffect(() => {
    fastRef.current = fast;
    if (videoRef.current) videoRef.current.playbackRate = fast ? 2 : 1;
  }, [fast]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // Cambio de historia: animación + cargar medio.
  useEffect(() => {
    elapsed.current = 0;
    retry.current = 0;
    setFailed(false);
    stageRef.current?.animate(
      [
        { opacity: 0, transform: `translateX(${dirRef.current * 22}%) scale(.94)` },
        { opacity: 1, transform: "translateX(0) scale(1)" },
      ],
      { duration: 320, easing: "cubic-bezier(.2,.8,.2,1)" }
    );
    const v = videoRef.current;
    if (!v) return;
    if (isVideo) {
      setLoading(true);
      v.muted = !soundOn;
      v.src = withV(cur.media);
      v.playbackRate = fastRef.current ? 2 : 1;
      v.load();
      v.play().catch(() => {
        v.muted = true;
        setSoundOn(false);
        v.play().catch(() => {});
      });
    } else {
      setLoading(false);
      v.pause();
      v.removeAttribute("src");
      v.load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Precarga la siguiente historia.
  useEffect(() => {
    const nx = items[active + 1];
    if (nx && nx.mediaType !== "video") new Image().src = nx.media;
  }, [active, items]);

  // Barra de progreso fluida (sin re-render).
  useEffect(() => {
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = now - last;
      last = now;
      let pct = 0;
      const v = videoRef.current;
      if (isVideo && v) {
        pct = v.duration ? (v.currentTime / v.duration) * 100 : 0;
      } else if (!failed) {
        if (!press.current || fastRef.current) elapsed.current += dt * (fastRef.current ? 2 : 1);
        pct = (elapsed.current / IMG_MS) * 100;
        if (pct >= 100) {
          go(1);
          return;
        }
      }
      if (barRef.current) barRef.current.style.width = `${Math.min(100, pct)}%`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, isVideo, failed, go]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const onVideoError = () => {
    const v = videoRef.current;
    if (!v || !isVideo) return;
    if (retry.current < 2) {
      retry.current += 1;
      v.src = withV(cur.media) + `&r=${Date.now()}`;
      v.load();
      v.play().catch(() => {});
    } else {
      setLoading(false);
      setFailed(true);
    }
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    const on = !soundOn;
    setSoundOn(on);
    if (v) {
      v.muted = !on;
      v.play().catch(() => {});
    }
  };

  const onDown = (e) => {
    press.current = { x: e.clientX, y: e.clientY, t: Date.now(), held: false };
    press.current.timer = setTimeout(() => {
      if (press.current) {
        press.current.held = true;
        setFast(true);
      }
    }, HOLD_MS);
  };
  const onUp = (e) => {
    const p = press.current;
    press.current = null;
    if (!p) return;
    clearTimeout(p.timer);
    if (p.held) {
      setFast(false);
      return;
    }
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    if (dy > 90 && Math.abs(dy) > Math.abs(dx)) return onClose();
    if (Math.abs(dx) > 50) return go(dx < 0 ? 1 : -1);
    go(e.clientX < window.innerWidth / 3 ? -1 : 1);
  };
  const onCancel = () => {
    if (press.current) clearTimeout(press.current.timer);
    press.current = null;
    setFast(false);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] select-none overflow-hidden bg-black"
      style={{ height: "100dvh", width: "100vw", touchAction: "none" }}
      data-testid="story-viewer"
    >
      <div ref={stageRef} className="absolute inset-0 flex items-center justify-center">
        <video
          ref={videoRef}
          poster={isVideo ? cur.thumb || undefined : undefined}
          className={`h-full w-full object-contain ${isVideo ? "" : "hidden"}`}
          playsInline
          muted
          autoPlay
          preload="auto"
          onWaiting={() => setLoading(true)}
          onPlaying={() => setLoading(false)}
          onCanPlay={() => setLoading(false)}
          onEnded={() => go(1)}
          onError={onVideoError}
          data-testid="story-video"
        />
        {!isVideo && cur && (
          <img src={cur.media} alt="" draggable={false} className="h-full w-full object-contain" data-testid="story-image" />
        )}
      </div>

      <div className="absolute left-0 right-0 top-0 z-20 flex gap-1 p-3 pt-[max(12px,env(safe-area-inset-top))]">
        {items.map((_, i) => (
          <div key={`${i}-${active}`} className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/30">
            <div
              ref={i === active ? barRef : null}
              className="h-full bg-white"
              style={{ width: i < active ? "100%" : "0%" }}
            />
          </div>
        ))}
      </div>

      <div
        className="absolute inset-0 z-10"
        onPointerDown={onDown}
        onPointerUp={onUp}
        onPointerCancel={onCancel}
        onPointerLeave={(e) => press.current && onUp(e)}
        onContextMenu={(e) => e.preventDefault()}
        data-testid="story-tap-layer"
      />

      <div className="absolute right-3 top-[max(28px,calc(env(safe-area-inset-top)+20px))] z-30 flex gap-2">
        {isVideo && (
          <button
            onClick={toggleSound}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur"
            data-testid="story-sound-btn"
          >
            {soundOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
          </button>
        )}
        <button
          onClick={onClose}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur"
          data-testid="story-close-btn"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {fast && (
        <span className="pointer-events-none absolute left-1/2 top-16 z-30 -translate-x-1/2 rounded-full bg-black/55 px-3 py-1 text-sm font-bold text-white backdrop-blur" data-testid="story-x2">
          2x ▶▶
        </span>
      )}
      {loading && !failed && (
        <span className="pointer-events-none absolute left-1/2 top-1/2 z-20 h-10 w-10 -translate-x-1/2 -translate-y-1/2 animate-spin rounded-full border-2 border-white/25 border-t-white" />
      )}
      {failed && (
        <p className="pointer-events-none absolute inset-x-6 top-1/2 z-20 -translate-y-1/2 text-center text-sm text-white/80">
          No se pudo cargar este video. Toca a la derecha para continuar.
        </p>
      )}
      {cur?.caption && (
        <p className="pointer-events-none absolute bottom-10 left-0 right-0 z-20 px-6 text-center text-base text-white drop-shadow-lg">
          {cur.caption}
        </p>
      )}
    </div>,
    document.body
  );
};

export const StoriesBlock = ({ block }) => {
  const items = (block.items || []).filter((s) => s.media);
  const [open, setOpen] = useState(-1);
  const close = useCallback(() => setOpen(-1), []);
  return (
    <div>
      {block.title && (
        <h3 className="mb-2.5 text-center font-display text-xs font-bold uppercase tracking-[0.2em] text-white/60">
          {block.title}
        </h3>
      )}
      <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-none">
        {items.map((s, i) => (
          <StoryThumb key={s.id} s={s} i={i} onOpen={setOpen} />
        ))}
      </div>
      {open >= 0 && <StoryViewer items={items} start={open} onClose={close} />}
    </div>
  );
};
